import * as yup from 'yup';

// Raw API schemas
export const apiLocationSchema = yup.object({
  city: yup.string().required(),
  state: yup.string().nullable().defined(),
  country: yup.string().required(),
});

export const apiContactFieldSchema = yup.object({
  value: yup.string().required(),
  type: yup.string().required(),
});

export const apiFundingRoundSchema = yup.object({
  amount: yup.number().nullable().defined(),
  type: yup.string().nullable().defined(),
  date: yup.string().nullable().defined(),
  investors: yup.array(yup.string().required()).required(),
});

export const apiExperienceSchema = yup.object({
  company: yup.string().nullable().defined(),
  title: yup.string().nullable().defined(),
  startDate: yup.string().nullable().defined(),
  endDate: yup.string().nullable().defined(),
  current: yup.boolean().required(),
});

export const apiEducationSchema = yup.object({
  school: yup.string().nullable().defined(),
  degree: yup.string().nullable().defined(),
  startDate: yup.string().nullable().defined(),
  endDate: yup.string().nullable().defined(),
});

export const apiCompanyRowSchema = yup.object({
  id: yup.string().required(),
  listId: yup.string().required(),
  name: yup.string().required(),
  shortDescription: yup.string().nullable().defined(),
  longDescription: yup.string().nullable().defined(),
  website: yup.string().nullable().defined(),
  domain: yup.string().nullable().defined(),
  domainAliases: yup.array(yup.string().required()).required(),
  sector: yup.string().nullable().defined(),
  industry: yup.string().nullable().defined(),
  legalName: yup.string().nullable().defined(),
  ceoName: yup.string().nullable().defined(),
  primaryLocation: apiLocationSchema.nullable().defined(),
  secondaryLocation: yup.array(apiLocationSchema.required()).required(),
  foundedYear: yup.number().nullable().defined(),
  aliases: yup.array(yup.string().required()).required(),
  emails: yup.array(apiContactFieldSchema.required()).required(),
  phones: yup.array(apiContactFieldSchema.required()).required(),
  headcount: yup.string().nullable().defined(),
  acquired: yup.boolean().required(),
  acquiredBy: yup.string().nullable().defined(),
  logoUrl: yup.string().nullable().defined(),
  linkedinUrl: yup.string().nullable().defined(),
  linkedinAliases: yup.array(yup.string().required()).required(),
  twitterUrl: yup.string().nullable().defined(),
  facebookUrl: yup.string().nullable().defined(),
  instagramUrl: yup.string().nullable().defined(),
  crunchbaseUrl: yup.string().nullable().defined(),
  pitchbookUrl: yup.string().nullable().defined(),
  numFundingRounds: yup.number().nullable().defined(),
  latestFundingStage: yup.string().nullable().defined(),
  annualRevenue: yup.number().nullable().defined(),
  totalFunding: yup.number().nullable().defined(),
  lastFundingAmount: yup.number().nullable().defined(),
  lastFundingType: yup.string().nullable().defined(),
  lastFundingDate: yup.string().nullable().defined(),
  latestInvestors: yup.array(yup.string().required()).required(),
  investors: yup.array(yup.string().required()).required(),
  founders: yup.array(yup.string().required()).required(),
  rounds: yup.array(apiFundingRoundSchema.required()).required(),
  socials: yup.mixed<Record<string, string>>().required(),
  addedAt: yup.string().nullable().defined(),
});

export const apiPersonRowSchema = yup.object({
  id: yup.string().required(),
  listId: yup.string().required(),
  firstName: yup.string().nullable().defined(),
  lastName: yup.string().nullable().defined(),
  fullName: yup.string().required(),
  gender: yup.string().nullable().defined(),
  birthDate: yup.string().nullable().defined(),
  headline: yup.string().nullable().defined(),
  biography: yup.string().nullable().defined(),
  city: yup.string().nullable().defined(),
  state: yup.string().nullable().defined(),
  country: yup.string().nullable().defined(),
  location: apiLocationSchema.nullable().defined(),
  linkedinUrl: yup.string().nullable().defined(),
  linkedinAliases: yup.array(yup.string().required()).required(),
  facebookUrl: yup.string().nullable().defined(),
  twitterUrl: yup.string().nullable().defined(),
  githubUrl: yup.string().nullable().defined(),
  imageUrl: yup.string().nullable().defined(),
  emails: yup.array(apiContactFieldSchema.required()).required(),
  phones: yup.array(apiContactFieldSchema.required()).required(),
  experience: yup.array(apiExperienceSchema.required()).required(),
  education: yup.array(apiEducationSchema.required()).required(),
  companyDomain: yup.string().nullable().defined(),
  title: yup.string().nullable().defined(),
  functions: yup.array(yup.string().required()).required(),
  seniority: yup.string().nullable().defined(),
  department: yup.string().nullable().defined(),
  socials: yup.mixed<Record<string, string>>().required(),
  addedAt: yup.string().nullable().defined(),
});

export type IApiLocation = yup.InferType<typeof apiLocationSchema>;
export type IApiContactField = yup.InferType<typeof apiContactFieldSchema>;
export type IApiFundingRound = yup.InferType<typeof apiFundingRoundSchema>;
export type IApiExperience = yup.InferType<typeof apiExperienceSchema>;
export type IApiEducation = yup.InferType<typeof apiEducationSchema>;
export type IApiCompanyRow = yup.InferType<typeof apiCompanyRowSchema>;
export type IApiPersonRow = yup.InferType<typeof apiPersonRowSchema>;

function formatLocation(loc: IApiLocation | null): string | null {
  if (!loc) {
    return null;
  }
  return [loc.city, loc.state, loc.country].filter(Boolean).join(', ');
}

export const sourcingNewsItemSchema = yup.object({
  source: yup.string().required(),
  date: yup.string().required(),
  title: yup.string().required(),
  url: yup.string().required(),
});

export const sourcingCompanyRowSchema = yup
  .object({
    id: yup.string().required(),
    listId: yup.string().required(),
    enrichmentId: yup.string().nullable().default(null),
    acquired: yup.boolean().required(),
    acquiredBy: yup.string().nullable().default(null),
    aliases: yup.array(yup.string().required()).nullable().default(null),
    allInvestors: yup.array(yup.string().required()).nullable().default(null),
    annualRevenue: yup.number().nullable().default(null),
    ceoName: yup.string().nullable().default(null),
    crunchbaseUrl: yup.string().nullable().default(null),
    domain: yup.string().nullable().default(null),
    emails: yup.array(yup.string().required()).nullable().default(null),
    facebookUrl: yup.string().nullable().default(null),
    foundedYear: yup.number().nullable().default(null),
    founders: yup.array(yup.string().required()).nullable().default(null),
    headcount: yup.string().nullable().default(null),
    industry: yup.string().nullable().default(null),
    lastEnrichmentRequestId: yup.string().nullable().default(null),
    lastFundingRaised: yup.number().nullable().default(null),
    latestFundingStage: yup.string().nullable().default(null),
    legalName: yup.string().nullable().default(null),
    linkedinUrl: yup.string().nullable().default(null),
    logoUrl: yup.string().nullable().default(null),
    longDescription: yup.string().nullable().default(null),
    name: yup.string().required(),
    numFundingRounds: yup.number().nullable().default(null),
    phones: yup.array(yup.string().required()).nullable().default(null),
    pitchbookUrl: yup.string().nullable().default(null),
    postValuation: yup.number().nullable().default(null),
    primaryLocation: yup.string().nullable().default(null),
    recentNews: yup.array(sourcingNewsItemSchema.required()).nullable().default(null),
    secondaryLocation: yup.string().nullable().default(null),
    sector: yup.string().nullable().default(null),
    shortDescription: yup.string().nullable().default(null),
    totalFunding: yup.number().nullable().default(null),
    twitterUrl: yup.string().nullable().default(null),
    website: yup.string().nullable().default(null),
  })
  .transform((_val, orig: IApiCompanyRow) => ({
    id: orig.id,
    listId: orig.listId,
    enrichmentId: null,
    acquired: orig.acquired,
    acquiredBy: orig.acquiredBy,
    aliases: orig.aliases.length > 0 ? orig.aliases : null,
    allInvestors: orig.investors.length > 0 ? orig.investors : null,
    annualRevenue: orig.annualRevenue,
    ceoName: orig.ceoName,
    crunchbaseUrl: orig.crunchbaseUrl,
    domain: orig.domain,
    emails: orig.emails.length > 0 ? orig.emails.map((e) => e.value) : null,
    facebookUrl: orig.facebookUrl,
    foundedYear: orig.foundedYear,
    founders: orig.founders.length > 0 ? orig.founders : null,
    headcount: orig.headcount,
    industry: orig.industry,
    lastEnrichmentRequestId: null,
    lastFundingRaised: orig.lastFundingAmount,
    latestFundingStage: orig.latestFundingStage,
    legalName: orig.legalName,
    linkedinUrl: orig.linkedinUrl,
    logoUrl: orig.logoUrl,
    longDescription: orig.longDescription,
    name: orig.name,
    numFundingRounds: orig.numFundingRounds,
    phones: orig.phones.length > 0 ? orig.phones.map((p) => p.value) : null,
    pitchbookUrl: orig.pitchbookUrl,
    postValuation: null,
    primaryLocation: formatLocation(orig.primaryLocation),
    recentNews: null, // API does not return news data; populated by a separate endpoint if needed
    secondaryLocation: orig.secondaryLocation.length > 0 ? formatLocation(orig.secondaryLocation[0]) : null,
    sector: orig.sector,
    shortDescription: orig.shortDescription,
    totalFunding: orig.totalFunding,
    twitterUrl: orig.twitterUrl,
    website: orig.website,
  }));

export const sourcingPersonRowSchema = yup
  .object({
    id: yup.string().required(),
    listId: yup.string().required(),
    fullName: yup.string().required(),
    firstName: yup.string().nullable().default(null),
    lastName: yup.string().nullable().default(null),
    title: yup.string().nullable().default(null),
    headline: yup.string().nullable().default(null),
    department: yup.string().nullable().default(null),
    seniority: yup.string().nullable().default(null),
    companyDomain: yup.string().nullable().default(null),
    city: yup.string().nullable().default(null),
    state: yup.string().nullable().default(null),
    country: yup.string().nullable().default(null),
    linkedinUrl: yup.string().nullable().default(null),
    githubUrl: yup.string().nullable().default(null),
    twitterUrl: yup.string().nullable().default(null),
    facebookUrl: yup.string().nullable().default(null),
    imageUrl: yup.string().nullable().default(null),
    emails: yup.array(yup.string().required()).nullable().default(null),
    phones: yup.array(yup.string().required()).nullable().default(null),
    addedAt: yup.string().nullable().default(null),
  })
  .transform((_val, orig: IApiPersonRow) => ({
    id: orig.id,
    listId: orig.listId,
    fullName: orig.fullName,
    firstName: orig.firstName,
    lastName: orig.lastName,
    title: orig.title,
    headline: orig.headline,
    department: orig.department,
    seniority: orig.seniority,
    companyDomain: orig.companyDomain,
    city: orig.city ?? orig.location?.city ?? null,
    state: orig.state ?? orig.location?.state ?? null,
    country: orig.country ?? orig.location?.country ?? null,
    linkedinUrl: orig.linkedinUrl,
    githubUrl: orig.githubUrl,
    twitterUrl: orig.twitterUrl,
    facebookUrl: orig.facebookUrl,
    imageUrl: orig.imageUrl,
    emails: orig.emails.length > 0 ? orig.emails.map((e) => e.value) : null,
    phones: orig.phones.length > 0 ? orig.phones.map((ph) => ph.value) : null,
    addedAt: orig.addedAt,
  }));

export type ISourcingNewsItem = yup.InferType<typeof sourcingNewsItemSchema>;
export type ISourcingCompanyRow = yup.InferType<typeof sourcingCompanyRowSchema>;
export type ISourcingPersonRow = yup.InferType<typeof sourcingPersonRowSchema>;

// ── Add to list request schemas ──────────────────────────────────────────────
export const addCompanyToListRequestSchema = yup.object({
  name: yup.string().optional(),
  domain: yup.string().optional(),
  website: yup.string().optional(),
  sector: yup.string().optional(),
  industry: yup.string().optional(),
  linkedinUrl: yup.string().optional(),
  // Backend persists these when present so the grid is fully populated without a manual Enrich Now.
  shortDescription: yup.string().optional(),
  logoUrl: yup.string().optional(),
  headcount: yup.string().optional(),
  foundedYear: yup.number().optional(),
});

export const addPersonToListRequestSchema = yup.object({
  firstName: yup.string().optional(),
  lastName: yup.string().optional(),
  fullName: yup.string().optional(),
  email: yup.string().optional(),
  linkedinUrl: yup.string().optional(),
  companyDomain: yup.string().optional(),
  title: yup.string().optional(),
});

export type IAddCompanyToListRequest = yup.InferType<typeof addCompanyToListRequestSchema>;
export type IAddPersonToListRequest = yup.InferType<typeof addPersonToListRequestSchema>;

// ── Enrich request schemas ───────────────────────────────────────────────────
export const enrichCompanyRequestSchema = yup.object({
  domain: yup.string().optional(),
  linkedinUrl: yup.string().optional(),
  crunchbaseUrl: yup.string().optional(),
  name: yup.string().optional(),
});

export const enrichPersonRequestSchema = yup.object({
  email: yup.string().optional(),
  linkedinUrl: yup.string().optional(),
});

export const enrichmentResponseSchema = yup.object({
  enrichmentRequestId: yup.string().required(),
  status: yup.string().required(),
  entityCount: yup.number().required(),
});

export type IEnrichCompanyRequest = yup.InferType<typeof enrichCompanyRequestSchema>;
export type IEnrichPersonRequest = yup.InferType<typeof enrichPersonRequestSchema>;
export type IEnrichmentResponse = yup.InferType<typeof enrichmentResponseSchema>;
